import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The sound a notification makes.
 *
 * Synthesised rather than shipped as a file. A short chime is a handful of
 * oscillator notes, and generating it avoids an audio asset in every bundle,
 * a fetch that can fail, and the strict-CSP question of where it is served
 * from. It also means the urgent variant can simply be the same shape played
 * differently, instead of a second file that has to be kept in tune with it.
 *
 * Two rules, both learned the hard way by everyone who has shipped this:
 *
 *   A browser will not make noise until the person has interacted with the
 *   page. Trying anyway throws, and on some browsers leaves the audio context
 *   wedged. So the context is created lazily on the first play and resumed if
 *   it was suspended.
 *
 *   Sound is off unless switched on. People work in shared rooms, and a tool
 *   that starts chiming on its own is a tool they mute at the OS level, which
 *   loses the alerts that mattered.
 */

export type SoundTone = 'chime' | 'urgent';

function storageKey(portal: string): string {
  return `gbv:${portal}:notification-sound`;
}

/** Notes per tone: [frequency in Hz, start offset, length] for each blip. */
const TONES: Record<SoundTone, Array<[number, number, number]>> = {
  // A rising two-note chime — friendly, over in a third of a second.
  chime: [
    [880, 0, 0.16],
    [1318.5, 0.11, 0.22],
  ],
  // Three flat repeats. Insistent without being an alarm; this is the one that
  // plays when something is due now.
  urgent: [
    [988, 0, 0.13],
    [988, 0.19, 0.13],
    [988, 0.38, 0.2],
  ],
};

export function useNotificationSound(portal: string) {
  const supported = typeof window !== 'undefined' && 'AudioContext' in window;
  const contextRef = useRef<AudioContext | null>(null);

  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(storageKey(portal)) === 'on';
  });

  const setPreference = useCallback(
    (next: boolean) => {
      setEnabled(next);
      window.localStorage.setItem(storageKey(portal), next ? 'on' : 'off');
    },
    [portal],
  );

  /**
   * Plays regardless of the preference — for the settings screen, where the
   * whole point of the button is to hear it before deciding.
   */
  const preview = useCallback(
    (tone: SoundTone = 'chime') => {
      if (!supported) return false;

      try {
        contextRef.current ??= new AudioContext();
        const ctx = contextRef.current;
        // Autoplay policy parks the context until a gesture unblocks it.
        if (ctx.state === 'suspended') void ctx.resume();

        const now = ctx.currentTime;
        for (const [hz, at, length] of TONES[tone]) {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();

          osc.type = 'sine';
          osc.frequency.value = hz;

          // An envelope rather than a bare on/off: a square-edged gate clicks,
          // and the click is the part people find unpleasant.
          const start = now + at;
          gain.gain.setValueAtTime(0.0001, start);
          gain.gain.exponentialRampToValueAtTime(0.18, start + 0.012);
          gain.gain.exponentialRampToValueAtTime(0.0001, start + length);

          osc.connect(gain).connect(ctx.destination);
          osc.start(start);
          osc.stop(start + length + 0.02);
        }
        return true;
      } catch {
        // No sound is never worth breaking the notification it belonged to.
        return false;
      }
    },
    [supported],
  );

  const play = useCallback(
    (tone: SoundTone = 'chime') => (enabled ? preview(tone) : false),
    [enabled, preview],
  );

  // Another tab may have changed the preference.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(portal)) setEnabled(e.newValue === 'on');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [portal]);

  // Release the hardware when the app unmounts.
  useEffect(
    () => () => {
      void contextRef.current?.close();
      contextRef.current = null;
    },
    [],
  );

  return { supported, enabled: supported && enabled, setPreference, play, preview };
}
